import React, { useContext, useEffect, useState } from "react";
import { LOAN_APPLICATION_STATE, LOAN_STATUS } from "../../../../config/config";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import ApplicationInfoMessage from "../ApplicationInfo/components/ApplicationInfoMessage";
import { modules } from "../../index";
import { useTheme } from "@material-ui/styles";
import { STATUS_MESSAGE_IDS } from "../ApplicationInfo/config";
import { assetTypes } from "../../../shared/config";
import { MainCircularProgress } from "../../../shared/components/MainCircularProgress";
import { ACTION_EXCEPTIONS } from "../../../../utils/getActionException";
import { useApplicationInfoVideoId } from "./hooks/useApplicationInfoVideoId";
import { useInfoMessagesVideoId } from "./hooks/useInfoMessagesVideoId";
import { ConfigContext } from "../../../../contexts/ConfigContext";
import { LenderName, LoanStatus } from "@nemuru-eng/js-api-client";
import { THEME_NAMES } from "../../../../config/themes/config";

export const ApplicationInfoVideoId = ({ disableDocumentation }) => {
  const {
    state: {
      loan,
      loanApplications,
      flowItem,
      currentFlow,
      lastActionsReference,
      lastActionsReferenceLastChange,
      pendingRequests,
      actionException,
    },
    dispatch,
  } = useContext(ProcessContext);
  const {
    state: { themeName },
  } = useContext(ConfigContext);

  const [state, setState] = useState({
    message: undefined,
    neededResponsesBeforeCheckModuleConditions: [],
    nextModules: undefined,
  });
  const { message, neededResponsesBeforeCheckModuleConditions, nextModules } =
    state;

  const getSelectedLenderName = ({ loan, loanApplications }) => {
    return (
      loan?.loan_application_selected?.lender_name ||
      loanApplications?.find(
        (la) =>
          la?.lender_fields?.loan_application_status?.state ===
          LOAN_APPLICATION_STATE.SELECTED
      )?.lender_name
    );
  };
  const isAllowedLenderForVideoId = (lenderName) =>
    lenderName === LenderName.CAIXABANK || lenderName === LenderName.NEMURU;

  const selectedLenderName = getSelectedLenderName({ loan, loanApplications });
  const allowedLenderForKycVideo =
    isAllowedLenderForVideoId(selectedLenderName);

  const {
    isLenderSelectedPendingApproval,
    modulesInvolvedDefinitions,
    isLenderApprovalStatusDifferentThanNo,
    areEditableRequiredFilesValidatedOrUploaded,
  } = useApplicationInfoVideoId(
    neededResponsesBeforeCheckModuleConditions,
    allowedLenderForKycVideo
  );

  const { messages } = useInfoMessagesVideoId({
    isKycVideoRelevant: allowedLenderForKycVideo,
  });

  const { palette } = useTheme();

  const {
    ANALYSING_END,
    ANALYSING_UPLOAD_DOCUMENTATION,
    ANALYSING_DOCUMENTS_UPLOADED,
    ANALYSING_SIGNATURE_NOT_AVAILABLE,
    ANALYSING_PENDING_APPROVAL,
    PREAPPROVED_DETACHED_SIGN_END,
    PREAPPROVED_SIGNATURE_REDIRECT,
    PREAPPROVED_SIGNATURE_REDIRECT_NO_DOCS_NEEDED,
    PREAPPROVED_DOCS_AND_SIGNATURE_REDIRECT,
    CONTRACT_SIGNED_END,
    CONTRACT_SIGNED_NO_DOCS_NEEDED_END,
    CONTRACT_SIGNED_DOCS_NEEDED,
    APPROVED_END,
    DENIED_END,
    DENIED_AUTO_CPC,
    PREAPPROVED_LOADING_REMAINING_ACTIONS,
    LOAN_ONBOARDINGS_ARE_NOT_COMPLETED,
    LENDER_HAS_BEEN_SELECTED_UPLOAD_DOCUMENTATION,
    LENDER_HAS_BEEN_SELECTED_DOCUMENTS_UPLOADED,
    PREAPPROVED_DOCUMENTS_UPLOADED_SIGNATURE_NOT_AVAILABLE,
    INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_FOR_SIGNATURE,
    LENDER_HAS_BEEN_SELECTED_PENDING_DOWN_PAYMENT_UPLOAD_DOCUMENTATION,
    LENDER_HAS_BEEN_SELECTED_VIDEO_ID_PENDING,
  } = STATUS_MESSAGE_IDS;

  const {
    STATUS_INITIAL,
    STATUS_ANALYSING,
    STATUS_LENDER_HAS_BEEN_SELECTED,
    STATUS_CONTRACT_SIGNED,
    STATUS_DOCS_APPROVED,
    STATUS_ACTIVE_TRIAL,
    STATUS_ACTIVE,
    STATUS_DENIED,
  } = LOAN_STATUS;

  useEffect(() => {
    if (conditionsToSkipModule) {
      onSkipModule();
    }
    const nextModulesIds = getNextModulesIds();

    calculateAndDispatchNeededRequestsOfFutureModulesInvolved(
      loan.status,
      nextModulesIds
    );
    setState((s) => ({
      ...s,
      nextModules: nextModulesIds,
      neededResponsesBeforeCheckModuleConditions,
    }));
  }, []);

  useEffect(() => {
    if (nextModules !== null && nextModules !== undefined) {
      setMessageIfNoMoreResponsesNeeded();
    }
  }, [message, pendingRequests, nextModules, lastActionsReferenceLastChange]);

  const conditionsToSkipModule =
    loan.asset_reference[0].type === assetTypes.CARD &&
    areEditableRequiredFilesValidatedOrUploaded === true &&
    loan.status === LoanStatus.STATUS_CONTRACT_SIGNED;

  const onSkipModule = () => {
    dispatch({
      type: "SET_MODULE_SKIPPED",
    });
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
  };

  const setMessageIfNoMoreResponsesNeeded = () => {
    let nextCompatibleModule = null;
    let message = null;
    let newNeededActions = neededResponsesBeforeCheckModuleConditions;
    const removeNeededResponsesWhenRequestIsResolved = () =>
      lastActionsReference.forEach(
        (action) =>
          (newNeededActions = neededResponsesBeforeCheckModuleConditions.filter(
            (actionName) =>
              `${actionName}_FULFILLED` !== action.type &&
              `${actionName}_REJECTED` !== action.type
          ))
      );
    if (
      lastActionsReference &&
      loan.status &&
      neededResponsesBeforeCheckModuleConditions?.length > 0
    ) {
      removeNeededResponsesWhenRequestIsResolved();
    }
    if (newNeededActions.length === 0) {
      const compatibleFutureModules = getCompatibleModules(nextModules);
      nextCompatibleModule =
        compatibleFutureModules && compatibleFutureModules[0];
      message = nextCompatibleModule
        ? messageIdsByStatusAndNextModuleInvolved[loan.status]
            .messageIdsByVisibleNextModuleInvolved[nextCompatibleModule]
        : messageIdsByStatusAndNextModuleInvolved[loan.status]
            ?.defaultMessageId;
      if (messages[message] && !messages[message].button) {
        dispatch({
          type: "UPDATE_PROCESS_CONTEXT",
          payload: {
            flowEnded: true,
          },
        });
      }
    }
    setState((s) => ({
      ...s,
      neededResponsesBeforeCheckModuleConditions: newNeededActions,
      message,
    }));
  };

  const getDeniedMessageOnConditions = () => {
    const isCPCAutoLoan =
      loan.asset_reference[0].type === assetTypes.AUTO &&
      themeName === THEME_NAMES.caixabank;

    switch (true) {
      case isCPCAutoLoan:
        return {
          defaultMessageId: DENIED_AUTO_CPC,
          messageIdsByVisibleNextModuleInvolved: null,
        };
      default:
        return {
          defaultMessageId: DENIED_END,
          messageIdsByVisibleNextModuleInvolved: null,
        };
    }
  };

  const getMessageByControlledActionExceptionAndStatus = (
    actionException,
    status
  ) => {
    if (status === STATUS_ANALYSING) {
      switch (actionException) {
        case ACTION_EXCEPTIONS.INVALID_LOAN_STATUS_FOR_SIGNATURE_CREATION_EXCEPTION:
        case ACTION_EXCEPTIONS.SIGNATURE_IS_NOT_AVAILABLE_EXCEPTION:
        case ACTION_EXCEPTIONS.LOAN_DOESNT_HAVE_LOAN_APPLICATION_WITH_LOAN_APPLICATION_STATUS_SELECTED_EXCEPTION:
          return {
            defaultMessageId: isLenderSelectedPendingApproval
              ? ANALYSING_PENDING_APPROVAL
              : ANALYSING_SIGNATURE_NOT_AVAILABLE,
            messageIdsByVisibleNextModuleInvolved: null,
          };
        default:
          return null;
      }
    }
    if (status === STATUS_INITIAL) {
      switch (actionException) {
        case ACTION_EXCEPTIONS.LOAN_ONBOARDINGS_ARE_NOT_COMPLETED_EXCEPTION:
          return {
            defaultMessageId: LOAN_ONBOARDINGS_ARE_NOT_COMPLETED,
            messageIdsByVisibleNextModuleInvolved: null,
          };
        default:
          return null;
      }
    }
    if (status === STATUS_LENDER_HAS_BEEN_SELECTED) {
      switch (actionException) {
        case ACTION_EXCEPTIONS.INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_STATUS_FOR_SIGNATURE_CREATION_EXCEPTION:
          return {
            defaultMessageId:
              INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_FOR_SIGNATURE,
            messageIdsByVisibleNextModuleInvolved:
              !areEditableRequiredFilesValidatedOrUploaded
                ? {
                    [modules.DOCUMENTATION.id]:
                      LENDER_HAS_BEEN_SELECTED_PENDING_DOWN_PAYMENT_UPLOAD_DOCUMENTATION,
                  }
                : null,
          };
        case ACTION_EXCEPTIONS.SIGNATURE_IS_NOT_AVAILABLE_EXCEPTION:
          return {
            defaultMessageId:
              PREAPPROVED_DOCUMENTS_UPLOADED_SIGNATURE_NOT_AVAILABLE,
            messageIdsByVisibleNextModuleInvolved: {
              [modules.DOCUMENTATION.id]:
                !areEditableRequiredFilesValidatedOrUploaded
                  ? LENDER_HAS_BEEN_SELECTED_UPLOAD_DOCUMENTATION
                  : LENDER_HAS_BEEN_SELECTED_DOCUMENTS_UPLOADED,
            },
          };
        default:
          return null;
      }
    }
    return null;
  };

  const getDefaultMessageIdByStatusWithConditions = (status) => {
    switch (status) {
      case STATUS_ANALYSING:
        if (isLenderSelectedPendingApproval) {
          return ANALYSING_PENDING_APPROVAL;
        }
        return ANALYSING_DOCUMENTS_UPLOADED;
      case STATUS_LENDER_HAS_BEEN_SELECTED:
        return PREAPPROVED_DETACHED_SIGN_END;
      case STATUS_CONTRACT_SIGNED:
        if (
          loan.asset_reference[0].type === assetTypes.CARD &&
          !areEditableRequiredFilesValidatedOrUploaded
        ) {
          return CONTRACT_SIGNED_DOCS_NEEDED;
        }
        if (disableDocumentation) return CONTRACT_SIGNED_NO_DOCS_NEEDED_END;
        return CONTRACT_SIGNED_END;
      default:
        break;
    }
  };

  const messageIdsByStatusAndNextModuleInvolved = {
    [STATUS_ANALYSING]: isLenderApprovalStatusDifferentThanNo
      ? getMessageByControlledActionExceptionAndStatus(
          actionException,
          STATUS_ANALYSING
        ) ?? {
          defaultMessageId:
            getDefaultMessageIdByStatusWithConditions(STATUS_ANALYSING),
          messageIdsByVisibleNextModuleInvolved: {
            [modules.DOCUMENTATION.id]:
              !areEditableRequiredFilesValidatedOrUploaded
                ? ANALYSING_UPLOAD_DOCUMENTATION
                : isLenderSelectedPendingApproval
                ? ANALYSING_PENDING_APPROVAL
                : ANALYSING_DOCUMENTS_UPLOADED,
          },
        }
      : {
          defaultMessageId: ANALYSING_END,
          messageIdsByVisibleNextModuleInvolved: null,
        },
    [STATUS_LENDER_HAS_BEEN_SELECTED]: pendingRequests?.find(
      (el) => el === "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID"
    )
      ? {
          defaultMessageId: PREAPPROVED_LOADING_REMAINING_ACTIONS,
        }
      : getMessageByControlledActionExceptionAndStatus(
          actionException,
          STATUS_LENDER_HAS_BEEN_SELECTED
        ) ?? {
          defaultMessageId: getDefaultMessageIdByStatusWithConditions(
            STATUS_LENDER_HAS_BEEN_SELECTED
          ),
          messageIdsByVisibleNextModuleInvolved: {
            [modules.DOCUMENTATION.id]: PREAPPROVED_DOCS_AND_SIGNATURE_REDIRECT,
            [modules.KYC_VIDEO_VERIFICATION.id]:
              LENDER_HAS_BEEN_SELECTED_VIDEO_ID_PENDING,
            [modules.OPTINS_AND_SIGNATURE.id]: disableDocumentation
              ? PREAPPROVED_SIGNATURE_REDIRECT_NO_DOCS_NEEDED
              : areEditableRequiredFilesValidatedOrUploaded
              ? PREAPPROVED_SIGNATURE_REDIRECT
              : PREAPPROVED_DOCS_AND_SIGNATURE_REDIRECT,
          },
        },
    [STATUS_CONTRACT_SIGNED]: {
      defaultMessageId: getDefaultMessageIdByStatusWithConditions(
        STATUS_CONTRACT_SIGNED
      ),
      messageIdsByVisibleNextModuleInvolved: {
        [modules.DOCUMENTATION.id]: !areEditableRequiredFilesValidatedOrUploaded
          ? CONTRACT_SIGNED_DOCS_NEEDED
          : getDefaultMessageIdByStatusWithConditions(STATUS_CONTRACT_SIGNED),
      },
    },
    [STATUS_DOCS_APPROVED]: {
      defaultMessageId: APPROVED_END,
      messageIdsByVisibleNextModuleInvolved: {
        [modules.DOCUMENTATION.id]: !areEditableRequiredFilesValidatedOrUploaded
          ? CONTRACT_SIGNED_DOCS_NEEDED
          : APPROVED_END,
      },
    },
    [STATUS_ACTIVE_TRIAL]: {
      defaultMessageId: APPROVED_END,
      messageIdsByVisibleNextModuleInvolved: null,
    },
    [STATUS_ACTIVE]: {
      defaultMessageId: APPROVED_END,
      messageIdsByVisibleNextModuleInvolved: null,
    },
    [STATUS_DENIED]: getDeniedMessageOnConditions(),
    [STATUS_INITIAL]: getMessageByControlledActionExceptionAndStatus(
      actionException,
      STATUS_INITIAL
    ) ?? {
      defaultMessageId: "ERROR",
    },
  };

  const dispatchNeededRequestsByModuleId = (moduleId) => {
    if (isModuleIdInModulesInvolvedOfCurrentState(moduleId))
      modulesInvolvedDefinitions[moduleId]?.requestNeededParamsToShow();
  };

  const checkModuleConditions = (moduleId) => {
    return modulesInvolvedDefinitions[moduleId]?.showConditions();
  };

  const getNextModulesIds = () => {
    const nextFlowItem = flowItem + 1;
    const nextModules = currentFlow.modules.slice(nextFlowItem);
    return nextModules.map((item) => item.id);
  };

  const isModuleIdInModulesInvolvedOfCurrentState = (moduleId) => {
    const messageIdsByVisibleNextModuleInvolved =
      messageIdsByStatusAndNextModuleInvolved[loan.status]
        ?.messageIdsByVisibleNextModuleInvolved;
    return !!messageIdsByVisibleNextModuleInvolved?.[moduleId];
  };

  const getCompatibleModules = (modulesList) => {
    if (!modulesList) return modulesList;
    return modulesList.filter(
      (moduleId) =>
        isModuleIdInModulesInvolvedOfCurrentState(moduleId) &&
        checkModuleConditions(moduleId)
    );
  };

  const calculateAndDispatchNeededRequestsOfFutureModulesInvolved = (
    status,
    futureModulesIds
  ) => {
    futureModulesIds.forEach((moduleId) =>
      dispatchNeededRequestsByModuleId(moduleId)
    );
    if (!neededResponsesBeforeCheckModuleConditions.length) {
      setMessageIfNoMoreResponsesNeeded();
    }
  };

  const messageButtonHandleClick = () => {
    dispatch({
      type: "SET_FLOW_ITEM_OK",
    });
  };

  const currentMessage = messages[message];

  return message ? (
    <ApplicationInfoMessage
      asset={currentMessage.asset}
      color={currentMessage.color}
      title={currentMessage.title}
      subtitle={currentMessage.subtitle}
      detail={currentMessage.detail}
      instructions={{
        title: currentMessage.instructions.title,
        listItems: currentMessage.instructions.listItems,
        titleDynamicDataTexts:
          currentMessage.instructions.titleDynamicDataTexts,
        style: currentMessage.instructions.style,
      }}
      button={currentMessage.button}
      infoMessage={currentMessage.infoMessage}
      handleClick={currentMessage.button && messageButtonHandleClick}
    />
  ) : (
    <MainCircularProgress color={palette.secondary.main} textId={"INFO_SYNC"} />
  );
};
